import { useState, useEffect, useMemo } from "react";
import settingsDefaults from "../settings.json";
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend
} from "recharts";

export default function DashboardPage() {
  // SETTINGS STATE (defaults from src/settings.json)
  
  // MRPL Capacities
  const [wllSolar, setWllSolar] = useState(settingsDefaults.WLL_Solar_Installed_Capacity);
  const [wllWind, setWllWind] = useState(settingsDefaults.WLL_Wind_Installed_Capacity);
  const [wclSolar, setWclSolar] = useState(settingsDefaults.WCL_Solar_Installed_Capacity);
  const [wclWind, setWclWind] = useState(settingsDefaults.WCL_Wind_Installed_Capacity);
  
  // MRPL Contracts & Loss
  const [wllContract, setWllContract] = useState(settingsDefaults.WLL_MRPL_Contract || 47);
  const [wclContract, setWclContract] = useState(settingsDefaults.WCL_MRPL_Contract || 42);
  const [lossPercent, setLossPercent] = useState(settingsDefaults.Loss_Percentage);
  
  // On-Site Solar (WLL only)
  const [onsiteSolarCap, setOnsiteSolarCap] = useState(settingsDefaults.Onsite_Solar_Capacity || 22);
  
  // Thermal Unit Parameters
  const [thermalMin, setThermalMin] = useState(settingsDefaults.Thermal_Tech_Min || 32.5);
  const [thermalMax, setThermalMax] = useState(settingsDefaults.Thermal_Max_Output || 75);
  const [rampRate, setRampRate] = useState(settingsDefaults.Thermal_Ramp_Rate || 15);
  const [aux325, setAux325] = useState(settingsDefaults.Thermal_Aux_32_5 || 0.13);
  const [aux50, setAux50] = useState(settingsDefaults.Thermal_Aux_50 || 0.11);
  const [aux75, setAux75] = useState(settingsDefaults.Thermal_Aux_75 || 0.10);
  
  // Must-Run Unit (WCL)
  const [mustRunGross, setMustRunGross] = useState(settingsDefaults.Must_Run_Gross || 12);
  const [mustRunAux, setMustRunAux] = useState(settingsDefaults.Must_Run_Aux || 1.3);
  
  // Probability Factors
  const [solarProb, setSolarProb] = useState(settingsDefaults.Solar_Probability_Factor);
  const [windProb, setWindProb] = useState(settingsDefaults.Wind_Probability_Factor);
  const [solarFactor1, setSolarFactor1] = useState(settingsDefaults.Solar_Factors.Factor1);
  const [solarFactor2, setSolarFactor2] = useState(settingsDefaults.Solar_Factors.Factor2);
  const [windFactor1, setWindFactor1] = useState(settingsDefaults.Wind_Factors.Factor1);
  const [windFactor2, setWindFactor2] = useState(settingsDefaults.Wind_Factors.Factor2);

  // DASHBOARD STATE
  const [selectedDate, setSelectedDate] = useState("");
  const [viewMode, setViewMode] = useState("day");
  const [masterData, setMasterData] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [wllDemandInput, setWllDemandInput] = useState(60); // Default WLL demand
  const [wclDemandInput, setWclDemandInput] = useState(70); // Default WCL demand
  
  const baseDate = new Date("2026-01-01T00:00:00");

  // LOAD SAVED SETTINGS & DATA
  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem("settings");
    if (savedSettings) {
      try {
        const s = JSON.parse(savedSettings);
        if (s.wllSolar !== undefined) setWllSolar(s.wllSolar);
        if (s.wllWind !== undefined) setWllWind(s.wllWind);
        if (s.wclSolar !== undefined) setWclSolar(s.wclSolar);
        if (s.wclWind !== undefined) setWclWind(s.wclWind);
        if (s.wllContract !== undefined) setWllContract(s.wllContract);
        if (s.wclContract !== undefined) setWclContract(s.wclContract);
        if (s.onsiteSolarCap !== undefined) setOnsiteSolarCap(s.onsiteSolarCap);
        if (s.thermalMin !== undefined) setThermalMin(s.thermalMin);
        if (s.thermalMax !== undefined) setThermalMax(s.thermalMax);
        if (s.rampRate !== undefined) setRampRate(s.rampRate);
        if (s.aux325 !== undefined) setAux325(s.aux325);
        if (s.aux50 !== undefined) setAux50(s.aux50);
        if (s.aux75 !== undefined) setAux75(s.aux75);
        if (s.mustRunGross !== undefined) setMustRunGross(s.mustRunGross);
        if (s.mustRunAux !== undefined) setMustRunAux(s.mustRunAux);
        if (s.solarProb) setSolarProb(s.solarProb);
        if (s.windProb) setWindProb(s.windProb);
        if (s.solarFactor1 !== undefined) setSolarFactor1(s.solarFactor1);
        if (s.solarFactor2 !== undefined) setSolarFactor2(s.solarFactor2);
        if (s.windFactor1 !== undefined) setWindFactor1(s.windFactor1);
        if (s.windFactor2 !== undefined) setWindFactor2(s.windFactor2);
        if (s.lossPercent !== undefined) setLossPercent(s.lossPercent);
      } catch (err) {
        console.warn("Invalid saved settings", err);
      }
    }

    // Load masterData from API
    fetch("https://shiny-dawn-cf6f.aditi-mumbai.workers.dev")
      .then(res => res.json())
      .then(data => {
        if (data.length > 0) {
          setMasterData(data);
        }
      })
      .catch(err => {
        console.error("DB load error:", err);
        // Fallback sample
        const sample = [
          { hour: 1, solar_P50: 0.20, solar_P75: 0.18, solar_P90: 0.15, wind_P50: 0.30, wind_P75: 0.28, wind_P90: 0.25, anjar_solar: 0.50 },
          { hour: 2, solar_P50: 0.25, solar_P75: 0.22, solar_P90: 0.18, wind_P50: 0.32, wind_P75: 0.30, wind_P90: 0.27, anjar_solar: 0.45 }
        ];
        setMasterData(sample);
      });
  }, []);

  // CSV UPLOAD HANDLER
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const raw = event.target?.result;
        if (!raw) return;

        const rows = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
          .split("\n").map(r => r.trim()).filter(Boolean);
        
        if (rows.length <= 1) {
          console.warn("CSV has no data rows");
          return;
        }

        const dataRows = rows.slice(1);
        const parsed = dataRows.map((row, i) => {
          const cols = row.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
          if (cols.length < 8) return null;

          return {
            hour: i + 1,
            solar_P50: parseFloat(cols[1]) || 0,
            solar_P75: parseFloat(cols[2]) || 0,
            solar_P90: parseFloat(cols[3]) || 0,
            wind_P50: parseFloat(cols[4]) || 0,
            wind_P75: parseFloat(cols[5]) || 0,
            wind_P90: parseFloat(cols[6]) || 0,
            anjar_solar: parseFloat(cols[7]) || 0
          };
        }).filter(Boolean);

        if (parsed.length === 0) {
          console.warn("Parsed CSV produced 0 valid rows");
          return;
        }

        setMasterData(parsed);
        
        fetch("https://shiny-dawn-cf6f.aditi-mumbai.workers.dev", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(parsed)
        })
          .then(() => alert("Uploaded to D1 database"))
          .catch(() => alert("Upload failed"));

        console.log("CSV parsed successfully. Rows:", parsed.length);
      } catch (err) {
        console.error("Error parsing CSV:", err);
      }
    };
    reader.readAsText(file);
  };

  // POWER FLOW CALCULATIONS
  
  // Helper: Calculate auxiliary percentage (interpolated)
  const calcAuxPercent = (gross) => {
    if (gross <= thermalMin) return aux325;
    if (gross <= 50) return aux325 + (aux50 - aux325) * (gross - thermalMin) / (50 - thermalMin);
    if (gross <= 75) return aux50 + (aux75 - aux50) * (gross - 50) / (75 - 50);
    return aux75;
  };

  const result = useMemo(() => {
    let thermalPrevGross = 50; // Initial thermal state
    const mustRunNet = mustRunGross - mustRunAux;

    return masterData.map((d, i) => {
      const solarKey = `solar_${solarProb}`;
      const windKey = `wind_${windProb}`;
      
      const dt = new Date(baseDate.getTime() + i * 3600000);
      const dd = String(dt.getDate()).padStart(2, "0");
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const yyyy = dt.getFullYear();
      const hh = String(dt.getHours()).padStart(2, "0");
      const date = `${dd}-${mm}-${yyyy}`;
      const month = `${mm}-${yyyy}`;
      const time = `${hh}:00`;

      const solarValue = d[solarKey] ?? 0;
      const windValue = d[windKey] ?? 0;
      const anjarRaw = d.anjar_solar ?? 0;

      // WLL & WCL MRPL GENERATION (with T&D loss)
      const WLL_MRPL_solar = solarValue * wllSolar * solarFactor1 * solarFactor2;
      const WLL_MRPL_wind = windValue * wllWind * windFactor1 * windFactor2;
      const WLL_MRPL_total = WLL_MRPL_solar + WLL_MRPL_wind;
      const WLL_MRPL_afterLoss = WLL_MRPL_total * (1 - lossPercent / 100);

      const WCL_MRPL_solar = solarValue * wclSolar * solarFactor1 * solarFactor2;
      const WCL_MRPL_wind = windValue * wclWind * windFactor1 * windFactor2;
      const WCL_MRPL_total = WCL_MRPL_solar + WCL_MRPL_wind;
      const WCL_MRPL_afterLoss = WCL_MRPL_total * (1 - lossPercent / 100);

      // WLL ON-SITE SOLAR (no transmission loss, scaled 20/22)
      const onsiteSolarGen = anjarRaw * (20 / 22);

      // WCL DISPATCH (determines thermal ramp need)
      const WCL_demand = wclDemandInput; // In real app, this would be hourly input
      const WCL_afterMustRun = Math.max(0, WCL_demand - mustRunNet);
      const WCL_fromMRPL = Math.min(WCL_afterMustRun, WCL_MRPL_afterLoss, wclSolar + wclWind);
      const WCL_deficitAfterMRPL = Math.max(0, WCL_afterMustRun - WCL_fromMRPL);

      // THERMAL UNIT (ramps based on WCL deficit)
      const thermalTargetGross = WCL_deficitAfterMRPL > 0 
        ? Math.min(thermalMax, WCL_deficitAfterMRPL / 0.89) 
        : thermalMin;
      
      const rampUpLimit = Math.min(thermalTargetGross, thermalPrevGross + rampRate);
      const rampDownLimit = Math.max(thermalTargetGross, thermalPrevGross - rampRate);
      const thermalGross = Math.max(thermalMin, Math.min(thermalMax, rampUpLimit, rampDownLimit));
      
      const auxPercent = calcAuxPercent(thermalGross);
      const auxMW = thermalGross * auxPercent;
      const thermalNet = thermalGross - auxMW;
      
      // Update for next iteration
      thermalPrevGross = thermalGross;

     // WCL TAKES THERMAL FIRST
       const WCL_fromThermal = Math.min(WCL_deficitAfterMRPL, thermalNet);
      const WCL_stillShort = Math.max(0, WCL_deficitAfterMRPL - WCL_fromThermal);

       // WLL DISPATCH
       const WLL_demand = wllDemandInput; // In real app, hourly input
      const WLL_fromOnsiteSolar = Math.min(onsiteSolarGen, WLL_demand, onsiteSolarCap);
      const WLL_afterOnsite = Math.max(0, WLL_demand - WLL_fromOnsiteSolar);
      const WLL_fromMRPL = Math.min(WLL_afterOnsite, WLL_MRPL_afterLoss, wllSolar + wllWind);
      const WLL_afterMRPL = Math.max(0, WLL_afterOnsite - WLL_fromMRPL);
      
      // WLL gets thermal EXCESS (what's left after WCL)
      const thermalLeftForWLL = Math.max(0, thermalNet - WCL_fromThermal);
      const WLL_fromThermal = Math.min(WLL_afterMRPL, thermalLeftForWLL);
      const WLL_unmet = Math.max(0, WLL_afterMRPL - WLL_fromThermal);

       // WCL BORROWS WLL MRPL EXCESS
      const WLL_MRPLexcess = Math.max(0, wllContract - WLL_fromMRPL);
      const WCL_fromWLLexcess = Math.min(WCL_stillShort, WLL_MRPLexcess);
      const WCL_unmet = Math.max(0, WCL_stillShort - WCL_fromWLLexcess);

      // MRPL EXCESS ACCOUNTING
      const WLL_myExcess = Math.max(0, wllContract - WLL_fromMRPL);
      const WCL_myExcess = Math.max(0, wclContract - WCL_fromMRPL);
      const WLL_toMRPL = Math.max(0, (wllSolar + wllWind) - WLL_fromMRPL);
      const WCL_toMRPL = Math.max(0, (wclSolar + wclWind) - WCL_fromMRPL);

      return {
        date, month, time,
        // MRPL Generation
        WLL_MRPL_solar, WLL_MRPL_wind, WLL_MRPL_total, WLL_MRPL_afterLoss,
        WCL_MRPL_solar, WCL_MRPL_wind, WCL_MRPL_total, WCL_MRPL_afterLoss,
        // On-site Solar
        onsiteSolarGen,
        // Thermal
        thermalGross, auxPercent, auxMW, thermalNet,
        // WCL Supply
        mustRunNet, WCL_fromMRPL, WCL_deficitAfterMRPL, WCL_fromThermal, 
        WCL_fromWLLexcess, WCL_unmet,
        // WLL Supply
        WLL_fromOnsiteSolar, WLL_fromMRPL, WLL_fromThermal, WLL_unmet,
        // Excess
        WLL_myExcess, WCL_myExcess, WLL_toMRPL, WCL_toMRPL,
        // Totals
        WLL_total: WLL_fromOnsiteSolar + WLL_fromMRPL + WLL_fromThermal,
        WCL_total: mustRunNet + WCL_fromMRPL + WCL_fromThermal + WCL_fromWLLexcess
      };
    });
  }, [
    masterData, wllSolar, wllWind, wclSolar, wclWind, wllContract, wclContract,
    onsiteSolarCap, thermalMin, thermalMax, rampRate, aux325, aux50, aux75,
    mustRunGross, mustRunAux, solarProb, windProb, solarFactor1, solarFactor2,
    windFactor1, windFactor2, lossPercent, wllDemandInput, wclDemandInput
  ]);

  // FILTERING BY DATE/MONTH
  const filtered = useMemo(() => {
    if (!selectedDate) return result;
    if (viewMode === "day") return result.filter(r => r.date === selectedDate);
    const [dd, mm, yyyy] = selectedDate.split("-");
    const monthKey = `${mm}-${yyyy}`;
    return result.filter(r => r.month === monthKey);
  }, [result, selectedDate, viewMode]);

  // CHART DATA
  const chartData = filtered.map(d => ({
    time: d.time,
    WLL_Total: d.WLL_total,
    WCL_Total: d.WCL_total,
    Thermal: d.thermalNet,
    WLL_MRPL: d.WLL_fromMRPL,
    WCL_MRPL: d.WCL_fromMRPL,
    OnSite: d.WLL_fromOnsiteSolar
  }));

  const avgWLL = chartData.length ? chartData.reduce((s, d) => s + d.WLL_Total, 0) / chartData.length : 0;
  const avgWCL = chartData.length ? chartData.reduce((s, d) => s + d.WCL_Total, 0) / chartData.length : 0;

  return (
    <div style={{ padding: "1rem" }}>
      <h1>Power Flow Dashboard</h1>
      
      {/* Settings & Demand Inputs */}
      <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <button onClick={() => setShowSettings(true)} style={{ padding: "8px 16px" }}>
        Settings
        </button>
        <div>
          <label style={{ marginRight: 8 }}>WLL Demand (MW):</label>
          <input 
            type="number" 
            value={wllDemandInput} 
            onChange={e => setWllDemandInput(+e.target.value)}
            style={{ width: 80 }}
          />
        </div>
        <div>
          <label style={{ marginRight: 8 }}>WCL Demand (MW):</label>
          <input 
            type="number" 
            value={wclDemandInput} 
            onChange={e => setWclDemandInput(+e.target.value)}
            style={{ width: 80 }}
          />
        </div>
      </div>

      {/* Date Filters */}
      <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", alignItems: "center" }}>
        <input
          type="date"
          onChange={e => {
            const v = e.target.value;
            if (!v) return setSelectedDate("");
            const parts = v.split("-");
            setSelectedDate(`${parts[2]}-${parts[1]}-${parts[0]}`);
          }}
        />
        <select value={viewMode} onChange={e => setViewMode(e.target.value)}>
          <option value="day">Day</option>
          <option value="month">Month</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: 14, color: "#555" }}>
          {masterData.length ? `Loaded hours: ${masterData.length}` : "No data loaded"}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
        <div style={{ padding: "1rem", background: "#f0f9ff", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "#0369a1" }}>WLL Avg Supply</div>
          <div style={{ fontSize: 24, fontWeight: "bold" }}>{avgWLL.toFixed(2)} MW</div>
        </div>
        <div style={{ padding: "1rem", background: "#fef3c7", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "#92400e" }}>WCL Avg Supply</div>
          <div style={{ fontSize: 24, fontWeight: "bold" }}>{avgWCL.toFixed(2)} MW</div>
        </div>
        <div style={{ padding: "1rem", background: "#dcfce7", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "#14532d" }}>Thermal Capacity</div>
          <div style={{ fontSize: 24, fontWeight: "bold" }}>{thermalMax} MW</div>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
        {/* WLL + WCL Total Supply */}
        <div style={{ background: "#fff", padding: "1rem", borderRadius: 8, border: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: "0 0 12px 0" }}>WLL & WCL Total Supply</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="WLL_Total" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="WCL_Total" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Thermal & MRPL Breakdown */}
        <div style={{ background: "#fff", padding: "1rem", borderRadius: 8, border: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: "0 0 12px 0" }}>Power Source Breakdown</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="OnSite" stackId="1" stroke="#fbbf24" fill="#fef3c7" />
              <Area type="monotone" dataKey="WLL_MRPL" stackId="1" stroke="#3b82f6" fill="#dbeafe" />
              <Area type="monotone" dataKey="WCL_MRPL" stackId="1" stroke="#f97316" fill="#fed7aa" />
              <Area type="monotone" dataKey="Thermal" stackId="1" stroke="#10b981" fill="#d1fae5" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
            <tr>
              <th style={{ padding: 8, textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Time</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>WLL MRPL</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>WCL MRPL</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>On-Site</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>Thermal</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>WLL Total</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>WCL Total</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>WLL Unmet</th>
              <th style={{ padding: 8, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>WCL Unmet</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 16, textAlign: "center", color: "#6b7280" }}>
                  No data to display. Upload CSV via Settings or select a date.
                </td>
              </tr>
            )}
            {filtered.map((d, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: 8 }}>{d.time}</td>
                <td style={{ padding: 8, textAlign: "right" }}>{d.WLL_fromMRPL.toFixed(2)}</td>
                <td style={{ padding: 8, textAlign: "right" }}>{d.WCL_fromMRPL.toFixed(2)}</td>
                <td style={{ padding: 8, textAlign: "right" }}>{d.WLL_fromOnsiteSolar.toFixed(2)}</td>
                <td style={{ padding: 8, textAlign: "right" }}>{d.thermalNet.toFixed(2)}</td>
                <td style={{ padding: 8, textAlign: "right", fontWeight: "bold" }}>{d.WLL_total.toFixed(2)}</td>
                <td style={{ padding: 8, textAlign: "right", fontWeight: "bold" }}>{d.WCL_total.toFixed(2)}</td>
                <td style={{ padding: 8, textAlign: "right", color: d.WLL_unmet > 0 ? "#dc2626" : "#6b7280" }}>
                  {d.WLL_unmet.toFixed(2)}
                </td>
                <td style={{ padding: 8, textAlign: "right", color: d.WCL_unmet > 0 ? "#dc2626" : "#6b7280" }}>
                  {d.WCL_unmet.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <>
          <div style={{
            position: "fixed",
            top: "5%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fff",
            padding: "1.5rem",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            zIndex: 1000,
            width: 600,
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)"
          }}>
            <h2 style={{ marginTop: 0 }}>âš™ï¸ System Settings</h2>
            
            <div style={{ display: "grid", gap: 12 }}>
              {/* MRPL Capacities */}
              <div style={{ background: "#f0f9ff", padding: 12, borderRadius: 8 }}>
                <h4 style={{ margin: "0 0 8px 0" }}>MRPL Hybrid Capacities</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12 }}>WLL Solar (MW)</label>
                    <input type="number" value={wllSolar} onChange={e => setWllSolar(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>WLL Wind (MW)</label>
                    <input type="number" value={wllWind} onChange={e => setWllWind(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>WCL Solar (MW)</label>
                    <input type="number" value={wclSolar} onChange={e => setWclSolar(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>WCL Wind (MW)</label>
                    <input type="number" value={wclWind} onChange={e => setWclWind(+e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Contracts & Loss */}
              <div style={{ background: "#fef3c7", padding: 12, borderRadius: 8 }}>
                <h4 style={{ margin: "0 0 8px 0" }}>MRPL Contracts & Loss</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12 }}>WLL Contract (MW)</label>
                    <input type="number" value={wllContract} onChange={e => setWllContract(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>WCL Contract (MW)</label>
                    <input type="number" value={wclContract} onChange={e => setWclContract(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>T&D Loss (%)</label>
                    <input type="number" step="0.001" value={lossPercent} onChange={e => setLossPercent(+e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Thermal Unit */}
              <div style={{ background: "#dcfce7", padding: 12, borderRadius: 8 }}>
                <h4 style={{ margin: "0 0 8px 0" }}>Thermal Unit (80 MW)</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12 }}>Tech Min (MW)</label>
                    <input type="number" step="0.1" value={thermalMin} onChange={e => setThermalMin(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Max Output (MW)</label>
                    <input type="number" step="0.1" value={thermalMax} onChange={e => setThermalMax(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Ramp Rate (MW/hr)</label>
                    <input type="number" value={rampRate} onChange={e => setRampRate(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Aux @ 32.5 MW (%)</label>
                    <input type="number" step="0.01" value={aux325 * 100} onChange={e => setAux325(+e.target.value / 100)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Aux @ 50 MW (%)</label>
                    <input type="number" step="0.01" value={aux50 * 100} onChange={e => setAux50(+e.target.value / 100)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Aux @ 75 MW (%)</label>
                    <input type="number" step="0.01" value={aux75 * 100} onChange={e => setAux75(+e.target.value / 100)} />
                  </div>
                </div>
              </div>

              {/* Must-Run & On-Site Solar */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ background: "#fae8ff", padding: 12, borderRadius: 8 }}>
                  <h4 style={{ margin: "0 0 8px 0" }}>Must-Run (WCL)</h4>
                  <div>
                    <label style={{ fontSize: 12 }}>Gross (MW)</label>
                    <input type="number" value={mustRunGross} onChange={e => setMustRunGross(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Aux (MW)</label>
                    <input type="number" step="0.1" value={mustRunAux} onChange={e => setMustRunAux(+e.target.value)} />
                  </div>
                </div>
                <div style={{ background: "#ffedd5", padding: 12, borderRadius: 8 }}>
                  <h4 style={{ margin: "0 0 8px 0" }}>On-Site Solar (WLL)</h4>
                  <div>
                    <label style={{ fontSize: 12 }}>Capacity (MW)</label>
                    <input type="number" value={onsiteSolarCap} onChange={e => setOnsiteSolarCap(+e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Probability Factors */}
              <div style={{ background: "#e0e7ff", padding: 12, borderRadius: 8 }}>
                <h4 style={{ margin: "0 0 8px 0" }}>Probability Factors</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12 }}>Solar Probability</label>
                    <select value={solarProb} onChange={e => setSolarProb(e.target.value)}>
                      <option value="P50">P50</option>
                      <option value="P75">P75</option>
                      <option value="P90">P90</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Wind Probability</label>
                    <select value={windProb} onChange={e => setWindProb(e.target.value)}>
                      <option value="P50">P50</option>
                      <option value="P75">P75</option>
                      <option value="P90">P90</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Solar Factor 1</label>
                    <input type="number" step="0.0001" value={solarFactor1} onChange={e => setSolarFactor1(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Solar Factor 2</label>
                    <input type="number" step="0.0001" value={solarFactor2} onChange={e => setSolarFactor2(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Wind Factor 1</label>
                    <input type="number" step="0.0001" value={windFactor1} onChange={e => setWindFactor1(+e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Wind Factor 2</label>
                    <input type="number" step="0.0001" value={windFactor2} onChange={e => setWindFactor2(+e.target.value)} />
                  </div>
                </div>
              </div>

              {/* CSV Upload */}
              <div style={{ background: "#f3f4f6", padding: 12, borderRadius: 8 }}>
                <label style={{ fontSize: 13, fontWeight: "bold", display: "block", marginBottom: 8 }}>
                  Upload Solar/Wind CSV (8760 hours)
                </label>
                <input type="file" accept=".csv" onChange={handleFileUpload} />
                <p style={{ fontSize: 11, color: "#6b7280", margin: "4px 0 0 0" }}>
                  Last column must be Anjar Solar
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setShowSettings(false)} style={{ padding: "8px 16px" }}>
                Cancel
              </button>
              <button
                style={{ padding: "8px 16px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 6 }}
                onClick={() => {
                  const settings = {
                    wllSolar, wllWind, wclSolar, wclWind, wllContract, wclContract,
                    onsiteSolarCap, thermalMin, thermalMax, rampRate, aux325, aux50, aux75,
                    mustRunGross, mustRunAux, solarProb, windProb, solarFactor1, solarFactor2,
                    windFactor1, windFactor2, lossPercent
                  };
                  localStorage.setItem("settings", JSON.stringify(settings));
                  alert("Settings saved!");
                  setShowSettings(false);
                }}
              >
                ðŸ’¾ Save Settings
              </button>
            </div>
          </div>

          {/* Overlay */}
          <div
            onClick={() => setShowSettings(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.4)",
              zIndex: 999
            }}
          />
        </>
      )}
    </div>
  );
}

